import { computePosition, flip, offset, shift } from "@floating-ui/dom";

function getTranslate(element: HTMLElement) {
  const style = window.getComputedStyle(element);
  const matrix = new DOMMatrixReadOnly(style.transform);
  return {
    translateX: matrix.m41,
    translateY: matrix.m42,
  };
}

type tooltipParams = {
  disabled?: boolean;
  compact?: boolean;
  content?: string;
  placement?: TooltipPlacement;
  offset?: number;
  hideOnClick?: boolean;
  animationDuration?: number;
  delay?: number;
  html?: boolean;
};

export function tooltip(node: HTMLElement, params: tooltipParams = {}) {
  let content = params.content || node.title || node.getAttribute("aria-label");
  let observer;

  if (content === null || content === "" || params.disabled) return;

  const placement = params.placement || "top";
  const offsetNos = params.offset || 0;
  const hideOnClick = typeof params.hideOnClick === "undefined"
    ? true
    : params.hideOnClick;
  const animationDuration = typeof params.animationDuration === "undefined"
    ? 100
    : params.animationDuration;
  const delay = typeof params.delay === "undefined" ? 100 : params.delay;
  const animationShift = 2;

  node.setAttribute("aria-label", content ?? "");
  node.title = "";

  let tooltip: HTMLDivElement | null = null;
  let tooltipContent: HTMLDivElement | null = null;
  let isBeingRemoved: ReturnType<typeof setTimeout> | null = null;
  let isBeingShown: ReturnType<typeof setTimeout> | null = null;
  let aboutToBeHidden: ReturnType<typeof setTimeout> | null = null;

  // showTooltip function debounce
  const showTooltip = async () => {
    if (aboutToBeHidden) {
      clearTimeout(aboutToBeHidden);
      aboutToBeHidden = null;
    }
    if (isBeingRemoved) {
      clearTimeout(isBeingRemoved);
      isBeingRemoved = null;
    }
    if (!isBeingShown) {
      isBeingShown = setTimeout(async () => {
        isBeingShown = null;
        if (!isBeingRemoved) await showTooltipOne();
      }, delay);
    }
  };

  const showTooltipOne = async () => {
    if (!tooltip) {
      tooltipContent = document.createElement("div");
      tooltipContent.className = "tooltip-content";
      tooltip = document.createElement("div");
      tooltip.style.padding = "4px";
      tooltip.className = "tooltip-cover";
      if (params.compact) tooltip.className += " compact";
      tooltip.style.position = "absolute";
      tooltip.style.transitionDuration = `${animationDuration}ms`;
      tooltip.style.willChange = "transform opacity";
      tooltip.style.transform = "translateX(0px)";
      tooltip.style.zIndex = "9999";
      if (params.html) tooltipContent.innerHTML = content || "";
      else tooltipContent.textContent = content;
      tooltip.setAttribute("role", "tooltip");
      tooltip.setAttribute("data-placement", placement);
      document.body.appendChild(tooltip);
      tooltip.appendChild(tooltipContent);
      tooltip.style.display = content === "" || content === null
        ? "none"
        : "block";
      tooltip.addEventListener("mouseenter", showTooltip);
      tooltip.addEventListener("mouseleave", hideTooltip);
      await update();
      if (placement === "right") {
        tooltip.animate([
          { opacity: .5, transform: `translateX(-${animationShift}px)` },
          { opacity: 1, transform: "translateX(0px)" },
        ], animationDuration);
      }
      if (placement === "top") {
        tooltip.animate([
          { opacity: .5, transform: `translateY(${animationShift}px)` },
          { opacity: 1, transform: "translateY(0px)" },
        ], animationDuration);
      }
    }
  };

  const hideTooltip = async () => {
    aboutToBeHidden = setTimeout(async () => {
      aboutToBeHidden = null;
      isBeingRemoved = setTimeout(() => {
        isBeingRemoved = null;
        if (tooltip) tooltip.remove();
        tooltip = null;
      }, animationDuration);
      if (isBeingShown) {
        clearTimeout(isBeingShown);
        isBeingShown = null;
      }
      if (!tooltip) return;
      const translate = getTranslate(tooltip);
      if (placement === "right") {
        tooltip.animate([
          { opacity: 1, transform: `translateX(${translate.translateX}px)` },
          { opacity: .5, transform: `translateX(-${animationShift}px)` },
        ], animationDuration);
      }
      if (placement === "top") {
        tooltip.animate([
          { opacity: 1, transform: `translateY(${translate.translateY}px)` },
          { opacity: .5, transform: `translateY(${animationShift}px)` },
        ], animationDuration);
      }
    }, 0);
  };

  const update = async () => {
    if (!tooltip) return;
    const { x, y } = await computePosition(node, tooltip, {
      placement,
      middleware: [
        shift({ padding: 5 }),
        offset({ mainAxis: offsetNos }),
        flip(),
      ],
    });
    Object.assign(tooltip.style, {
      left: `${x}px`,
      top: `${y}px`,
    });
  };

  node.addEventListener("mouseenter", showTooltip);
  node.addEventListener("mouseleave", hideTooltip);
  if (hideOnClick) {
    node.addEventListener("click", hideTooltip);
  }

  return {
    update: (newParams: tooltipParams) => {
      content = newParams.content || node.title ||
        node.getAttribute("aria-label");
    },
    destroy: () => {
      node.removeEventListener("mouseenter", showTooltip);
      node.removeEventListener("mouseleave", hideTooltip);
      node.removeEventListener("click", hideTooltip);
      if (tooltip) tooltip.remove();
    },
  };
}
